import { AuthenticationService } from "../../core/authenticationService/AuthenticationService";
import { CharacterService } from "../../core/characterService/CharacterService";
import {
  CharacterHeldMatchService,
  HeldCharacterMatch,
} from "../../core/characterHeldMatchService/CharacterHeldMatchService";
import { PartnershipService } from "../../core/partnershipService/PartnershipService";
import { StaticDataService } from "../../core/staticDataService/StaticDataService";
import { UserService } from "../../core/userService/UserService";
import { Character, CharacterHelper } from "../models/Character";
import { CharacterStory } from "../models/CharacterStory";
import {
  DEFAULT_CHARACTER_INSTANT_MATCH_PROBABILITY_MAX,
  DEFAULT_CHARACTER_INSTANT_MATCH_PROBABILITY_MIN,
  DEFAULT_CHARACTER_PLAY_DELAY_MAX_SECONDS,
  DEFAULT_CHARACTER_PLAY_DELAY_MIN_SECONDS,
  DEFAULT_CHARACTER_REVEAL_STAGGER_MAX_SECONDS,
  DEFAULT_CHARACTER_REVEAL_STAGGER_MIN_SECONDS,
} from "../models/Constants";
import { CoreError, CoreErrorCause } from "../models/CoreError";
import { Partnership, PartnershipHelper } from "../models/Partnership";
import { User } from "../models/User";
import { AnalyticsFeature } from "../analytics/analyticsFeature";
import { AnalyticsConstants } from "../../presentation/assets/constants/analytics.constants";

export type { HeldCharacterMatch };

/**
 * Bounds the sequential re-hold loop in `healOrphans`: it runs inside every
 * deck build, and a silently-failing write would otherwise regrow the same
 * orphan set forever. The remainder heals on subsequent builds.
 */
const MAX_HEALED_ORPHANS_PER_BUILD = 20;

export interface HoldMatchInput {
  userId: string;
  partnershipId: string;
  characterId: string;
  cardId: string;
}

export interface HealOrphansInput {
  userId: string;
  partnershipId: string;
  characterId: string;
  userLikedCardIds: string[];
  characterLikedCardIds: string[];
  matchedCardIds: string[];
}

export interface DiscoverableCharacters {
  /** Every active character, connected ones included, in catalog order. */
  characters: Character[];
  /**
   * characterId → partnershipId for the characters the user is already
   * playing with. Discover keeps those tiles in the grid (dimmed, with a
   * "keep playing" affordance) instead of hiding them.
   */
  connectedPartnershipIds: ReadonlyMap<string, string>;
  connectedCount: number;
}

/**
 * Fictional-character play: connect/disconnect, character selection, and the
 * held-match lifecycle that simulates the character playing their deck hours
 * later.
 *
 * Held matches live in `users/{uid}/characterHeldMatches` until the backend
 * scheduler commits them to the ordinary partnership matches path (and
 * notifies the user by web push / email); the app then reads back the
 * `revealedAt` markers to present the stories and clears them.
 */
export class CharactersFeature {
  private static get heldMatches(): CharacterHeldMatchService {
    return CharacterHeldMatchService.getInstance();
  }

  private static getAuthUserId(): string | null {
    return AuthenticationService.getInstance().userId;
  }

  private static getAuthUserIdOrThrow(): string {
    const userId = this.getAuthUserId();
    if (!userId) {
      throw new CoreError({
        cause: CoreErrorCause.Forbidden,
        context: "No authenticated user available for characters feature",
      });
    }
    return userId;
  }

  private static randomBetween(min: number, max: number): number {
    const upper = Math.max(max, min);
    return min + Math.random() * (upper - min);
  }

  // ==================== Discovery & connection ====================

  /**
   * Every active character for the Discover grid, plus which of them the user
   * is already playing with (and through which partnership). Connected
   * characters stay in the list so Discover can show them as "already yours"
   * and offer to resume. `connectedCount` comes back alongside because it is
   * computed here anyway and the Discover analytics need it — recomputing it
   * in the screen would mean a second full partnership load.
   */
  public static async loadDiscoverableCharacters(
    userId: string,
  ): Promise<DiscoverableCharacters> {
    const [characters, connectedPartnershipIds] = await Promise.all([
      CharacterService.getInstance().fetchCharacters(true),
      this.loadConnectedCharacterPartnershipIds(userId),
    ]);

    return {
      characters,
      connectedPartnershipIds,
      connectedCount: connectedPartnershipIds.size,
    };
  }

  /**
   * Avatar URLs of the first `limit` cast members, for the face stack on the
   * partners list's Discover card. Reads the cached catalog: the card is a
   * teaser, so a slightly stale list is fine and the list stays cheap to
   * open. Characters without a photo are skipped rather than rendered blank.
   */
  public static async loadCastAvatarPreview(limit = 4): Promise<string[]> {
    const characters = await CharacterService.getInstance().fetchCharacters();
    return characters
      .map((character) => CharacterHelper.getAvatarPhotoURL(character))
      .filter((url): url is string => typeof url === "string" && url.length > 0)
      .slice(0, Math.max(limit, 0));
  }

  public static async loadCharacter(
    characterId: string,
  ): Promise<Character | null> {
    return CharacterService.getInstance().loadCharacter(characterId);
  }

  /** Card ids that have a match story for this character (cached index). */
  public static async loadStoryCardIds(
    characterId: string,
  ): Promise<Set<string>> {
    return CharacterService.getInstance().loadCharacterStoryCardIds(
      characterId,
    );
  }

  /** Loads the match story for a (character, card) pair (cached). */
  public static async loadStory(
    characterId: string,
    cardId: string,
  ): Promise<CharacterStory | null> {
    return CharacterService.getInstance().loadCharacterStory(
      characterId,
      cardId,
    );
  }

  private static async loadCharacterPartnerships(
    userId: string,
  ): Promise<Partnership[]> {
    let user: User;
    try {
      // A brand-new account has an auth user before onboarding writes its
      // RTDB profile, and this runs from the auth-state change. No profile
      // simply means no partnerships yet — not an error worth reporting.
      user = await UserService.getInstance().loadUser(userId, {
        suppressMissingProfileReporting: true,
      });
    } catch (error) {
      if (
        error instanceof CoreError &&
        error.cause === CoreErrorCause.DataNotFound
      ) {
        return [];
      }
      throw error;
    }
    const partnerships = await Promise.all(
      user.validPartnershipsIdentifiers.map((partnershipId) =>
        PartnershipService.getInstance()
          .loadPartnership(partnershipId, userId)
          .catch(() => null),
      ),
    );
    return partnerships.filter(
      (partnership): partnership is Partnership =>
        !!partnership && PartnershipHelper.isCharacter(partnership),
    );
  }

  /**
   * Character ids the user currently has a live partnership with. Public
   * because the analytics user properties need the count — `User`'s
   * `validPartnershipsIdentifiers` merges every partnership category, so it
   * cannot be used to derive it.
   */
  public static async loadConnectedCharacterIds(
    userId: string,
  ): Promise<Set<string>> {
    const connectedPartnershipIds =
      await this.loadConnectedCharacterPartnershipIds(userId);
    return new Set(connectedPartnershipIds.keys());
  }

  /**
   * characterId → partnershipId for every live character partnership. The
   * partnership id is what the deck routes and the play-mode store key on,
   * so "keep playing" from Discover needs it next to the character.
   */
  public static async loadConnectedCharacterPartnershipIds(
    userId: string,
  ): Promise<Map<string, string>> {
    const characterPartnerships = await this.loadCharacterPartnerships(userId);
    const connectedPartnershipIds = new Map<string, string>();
    characterPartnerships.forEach((partnership) => {
      if (partnership.characterId) {
        connectedPartnershipIds.set(
          partnership.characterId,
          partnership.identifier,
        );
      }
    });
    return connectedPartnershipIds;
  }

  /** Whether the user currently has a live partnership with this character. */
  public static async isCharacterConnected(
    userId: string,
    characterId: string,
  ): Promise<boolean> {
    const connectedCharacterIds = await this.loadConnectedCharacterIds(userId);
    return connectedCharacterIds.has(characterId);
  }

  /**
   * Connects the user to a character: creates a fresh partnership and selects
   * it for play. If a live partnership with this character already exists it
   * is selected instead (defensive — connected characters get the resume
   * path via `selectCharacter`, not PLAY NOW).
   *
   * @returns The partnership identifier now selected for play
   */
  public static async connectToCharacter(characterId: string): Promise<string> {
    const userId = this.getAuthUserIdOrThrow();

    try {
      const characterPartnerships =
        await this.loadCharacterPartnerships(userId);
      const existing = characterPartnerships.find(
        (partnership) => partnership.characterId === characterId,
      );
      if (existing) {
        await PartnershipService.getInstance().selectCharacter(
          userId,
          characterId,
        );
        this.trackConnected(characterId, {
          wasAlreadyConnected: true,
          connectedCount: characterPartnerships.length,
        });
        return existing.identifier;
      }

      const partnershipId =
        await PartnershipService.getInstance().createCharacterPartnership(
          userId,
          characterId,
        );
      this.trackConnected(characterId, {
        wasAlreadyConnected: false,
        connectedCount: characterPartnerships.length + 1,
      });
      return partnershipId;
    } catch (error) {
      AnalyticsFeature.trackEvent(
        AnalyticsConstants.events.characterConnected,
        {
          [AnalyticsConstants.params.characterId]: characterId,
          [AnalyticsConstants.params.source]:
            AnalyticsConstants.values.sources.characterDetail,
          [AnalyticsConstants.params.result]:
            AnalyticsConstants.values.results.error,
          [AnalyticsConstants.params.errorCause]:
            AnalyticsFeature.getErrorCause(error),
        },
      );
      throw error;
    }
  }

  private static trackConnected(
    characterId: string,
    info: { wasAlreadyConnected: boolean; connectedCount: number },
  ): void {
    AnalyticsFeature.trackEvent(AnalyticsConstants.events.characterConnected, {
      [AnalyticsConstants.params.characterId]: characterId,
      [AnalyticsConstants.params.source]:
        AnalyticsConstants.values.sources.characterDetail,
      [AnalyticsConstants.params.wasAlreadyConnected]: info.wasAlreadyConnected,
      [AnalyticsConstants.params.connectedCount]: info.connectedCount,
      [AnalyticsConstants.params.result]:
        AnalyticsConstants.values.results.success,
    });
  }

  /** Selects an already-connected character for play. */
  public static async selectCharacter(characterId: string): Promise<void> {
    const userId = this.getAuthUserIdOrThrow();
    await PartnershipService.getInstance().selectCharacter(userId, characterId);
  }

  /**
   * Disconnects from a character partnership (client-side invalidation, like
   * a real-partner disconnect) and purges its held matches, so the backend
   * never reveals into an invalid partnership. The character becomes
   * discoverable again; reconnecting creates a brand-new partnership.
   */
  public static async disconnectFromCharacter(
    partnershipId: string,
    characterId: string,
    matchesCount?: number,
  ): Promise<void> {
    const userId = this.getAuthUserIdOrThrow();

    try {
      const user = await UserService.getInstance().loadUser(userId);
      const clearSelectedCharacter = user.selectedCharacter === characterId;
      await PartnershipService.getInstance().disconnectFromCharacterPartnership(
        partnershipId,
        userId,
        clearSelectedCharacter,
      );

      const removedEntries = await this.heldMatches.purgeForPartnership(
        userId,
        partnershipId,
      );

      // Counted after the disconnect so it reflects what the user is left with.
      const connectedCount = await this.loadConnectedCharacterIds(userId)
        .then((ids) => ids.size)
        .catch(() => undefined);

      AnalyticsFeature.trackEvent(
        AnalyticsConstants.events.characterDisconnected,
        {
          [AnalyticsConstants.params.characterId]: characterId,
          [AnalyticsConstants.params.partnershipId]: partnershipId,
          [AnalyticsConstants.params.source]:
            AnalyticsConstants.values.sources.matchesMenu,
          [AnalyticsConstants.params.heldCount]: removedEntries.length,
          [AnalyticsConstants.params.totalCards]: matchesCount,
          [AnalyticsConstants.params.connectedCount]: connectedCount,
          [AnalyticsConstants.params.result]:
            AnalyticsConstants.values.results.success,
        },
      );
    } catch (error) {
      AnalyticsFeature.trackEvent(
        AnalyticsConstants.events.characterDisconnected,
        {
          [AnalyticsConstants.params.characterId]: characterId,
          [AnalyticsConstants.params.partnershipId]: partnershipId,
          [AnalyticsConstants.params.source]:
            AnalyticsConstants.values.sources.matchesMenu,
          [AnalyticsConstants.params.totalCards]: matchesCount,
          [AnalyticsConstants.params.result]:
            AnalyticsConstants.values.results.error,
          [AnalyticsConstants.params.errorCause]:
            AnalyticsFeature.getErrorCause(error),
        },
      );
      throw error;
    }
  }

  // ==================== Held-match lifecycle ====================

  /**
   * Draws whether a character match is celebrated instantly. The probability
   * itself is re-drawn per swipe within remote-configured bounds, so the
   * instant/held split breathes instead of being a fixed coin.
   */
  public static drawInstantReveal(): boolean {
    const constants = StaticDataService.getInstance().getConstants();
    const min =
      constants?.characterInstantMatchProbabilityMin ??
      DEFAULT_CHARACTER_INSTANT_MATCH_PROBABILITY_MIN;
    const max =
      constants?.characterInstantMatchProbabilityMax ??
      DEFAULT_CHARACTER_INSTANT_MATCH_PROBABILITY_MAX;
    const probability = this.randomBetween(min, max);
    return Math.random() < probability;
  }

  /** Every hold of the partnership — pending and revealed-but-unseen alike. */
  public static async loadHeldMatches(
    userId: string,
    partnershipId: string,
  ): Promise<HeldCharacterMatch[]> {
    return this.heldMatches.loadForPartnership(userId, partnershipId);
  }

  /** Cards still waiting for their reveal (no `revealedAt` yet). */
  public static async getHeldCardIds(
    userId: string,
    partnershipId: string,
  ): Promise<Set<string>> {
    const entries = await this.loadHeldMatches(userId, partnershipId);
    return new Set(
      entries
        .filter((entry) => entry.revealedAtMs === undefined)
        .map((entry) => entry.cardId),
    );
  }

  /**
   * Holds a match for delayed reveal. The first held match of a batch draws
   * the character's "play time" hours from now; subsequent holds chain
   * 10–120 s after the batch's last reveal, so the matches later land as a
   * burst that reads like the character swiping their deck. The backend
   * commits the match and notifies the user when the time comes.
   */
  public static async holdMatch(input: HoldMatchInput): Promise<void> {
    const constants = StaticDataService.getInstance().getConstants();
    const playDelayMinMs =
      (constants?.characterPlayDelayMinSeconds ??
        DEFAULT_CHARACTER_PLAY_DELAY_MIN_SECONDS) * 1000;
    const playDelayMaxMs =
      (constants?.characterPlayDelayMaxSeconds ??
        DEFAULT_CHARACTER_PLAY_DELAY_MAX_SECONDS) * 1000;
    const staggerMinMs =
      (constants?.characterRevealStaggerMinSeconds ??
        DEFAULT_CHARACTER_REVEAL_STAGGER_MIN_SECONDS) * 1000;
    const staggerMaxMs =
      (constants?.characterRevealStaggerMaxSeconds ??
        DEFAULT_CHARACTER_REVEAL_STAGGER_MAX_SECONDS) * 1000;

    const now = Date.now();
    const pending = (
      await this.loadHeldMatches(input.userId, input.partnershipId)
    ).filter((entry) => entry.revealedAtMs === undefined);
    const lastScheduledForPartnership = pending.reduce(
      (latest, entry) => Math.max(latest, entry.revealAtMs),
      0,
    );
    const revealAtMs =
      lastScheduledForPartnership > now
        ? lastScheduledForPartnership +
          this.randomBetween(staggerMinMs, staggerMaxMs)
        : now + this.randomBetween(playDelayMinMs, playDelayMaxMs);

    const stored = await this.heldMatches.hold(input.userId, {
      partnershipId: input.partnershipId,
      characterId: input.characterId,
      cardId: input.cardId,
      revealAtMs,
      createdAtMs: now,
    });
    if (!stored) {
      // Dedupe hit — this (partnership, card) is already queued.
      return;
    }

    AnalyticsFeature.trackEvent(AnalyticsConstants.events.characterMatchHeld, {
      [AnalyticsConstants.params.characterId]: input.characterId,
      [AnalyticsConstants.params.cardId]: input.cardId,
      [AnalyticsConstants.params.partnershipId]: input.partnershipId,
      [AnalyticsConstants.params.delaySeconds]: Math.round(
        (revealAtMs - now) / 1000,
      ),
      [AnalyticsConstants.params.heldCount]:
        pending.filter((entry) => entry.revealAtMs > now).length + 1,
      [AnalyticsConstants.params.notificationResult]:
        AnalyticsConstants.values.notificationResults.queued,
    });
  }

  /**
   * Re-holds matches that exist in both liked sets but are neither matched
   * nor held — recovers holds lost to a closed tab between the like write and
   * the hold write, or to a failed write. Fresh reveal times are drawn; the
   * originals are unrecoverable.
   */
  public static async healOrphans(input: HealOrphansInput): Promise<void> {
    const characterLiked = new Set(input.characterLikedCardIds);
    const matched = new Set(input.matchedCardIds);
    const held = new Set(
      (await this.loadHeldMatches(input.userId, input.partnershipId)).map(
        (entry) => entry.cardId,
      ),
    );

    const orphanCardIds = input.userLikedCardIds.filter(
      (cardId) =>
        characterLiked.has(cardId) && !matched.has(cardId) && !held.has(cardId),
    );
    if (orphanCardIds.length === 0) {
      return;
    }

    const heldOrphans = orphanCardIds.slice(0, MAX_HEALED_ORPHANS_PER_BUILD);
    for (const cardId of heldOrphans) {
      await this.holdMatch({
        userId: input.userId,
        partnershipId: input.partnershipId,
        characterId: input.characterId,
        cardId,
      });
    }

    // Low-volume health signal: a persistent non-zero count means holds are
    // being lost between the like write and the hold write.
    AnalyticsFeature.trackEvent(
      AnalyticsConstants.events.characterMatchHealed,
      {
        [AnalyticsConstants.params.characterId]: input.characterId,
        [AnalyticsConstants.params.partnershipId]: input.partnershipId,
        [AnalyticsConstants.params.count]: orphanCardIds.length,
        [AnalyticsConstants.params.heldCount]: heldOrphans.length,
        [AnalyticsConstants.params.droppedCount]:
          orphanCardIds.length - heldOrphans.length,
      },
    );
  }

  /**
   * Clears the `revealedAt` markers once their stories were presented (or
   * the match surfaced through the live observer), so a reveal is shown once.
   */
  public static async markRevealsSeen(
    userId: string,
    partnershipId: string,
    cardIds: string[],
  ): Promise<void> {
    await this.heldMatches.remove(userId, partnershipId, cardIds);
  }
}
